<template>
  <div ref="thanksPage">
    <div v-if="showMoveModal" ref="activeModal">
      <booking-move-modal
        :item="activeItem"
        :item-ind="activeItemInd"
        :booking="booking"
        :booking-has-past-items="bookingHasPastItems"
        :saving="moveSaving"
        :error="moveError"
        @close="closeModal"
        @confirm="submitMove"
      />
    </div>
    <div v-else-if="showCancelModal" ref="activeModal">
      <booking-cancel-modal
        :booking="booking"
        :item="activeItem"
        :item-ind="activeItemInd"
        :mode="cancelMode"
        :saving="cancelSaving"
        :error="cancelError"
        @close="closeModal"
        @confirm="submitCancel"
      />
    </div>
    <div v-else class="book flex flex-wrap lg:flex-nowrap gap-8">
      <div class="w-full lg:w-3/5 flex-grow md:pr-4 lg:pb-20 flex flex-row gap-4 px-4 lg:px-0">
        <div>
          <div class="mb-4"><img :src="`${$settings.theme_url}/assets/images/thanks-whale.png`" alt="thanks whale"></div>
          <h1 class="font-bold text-3xl mb-4" data-testid="booking-thanks-title">{{ pageTitle }}</h1>

          <h3 class="mb-12">{{ pageIntro }} <span v-if="booking.orderNumber">Your order number is <span v-text="booking.orderNumber"></span></span></h3>

          <div v-if="actionSuccess" class="mb-6">
            <div class="rounded-2xl bg-light px-6 py-4 text-dark">
              {{ actionSuccess }}
            </div>
          </div>

          <div class="prose" v-html="textContent"></div>
        </div>
      </div>
      <div class="w-full lg:w-2/5 relative py-6 px-4 lg:px-0">
        <div v-if="!isCancelledBooking">
          <h2 class="text-2xl font-bold mb-8"
              :title="`Ordered at ${dateConfirmed}`"
              v-text="`Order number: ${booking.orderNumber ?? ''}`"></h2>

          <order-details
            @move-time="openMoveModal"
            @cancel-item="openCancelItemModal"
            @cancel-booking="openCancelBookingModal"
          />
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import moment from "moment";
import { mapState } from "pinia";
import { bookingItemsAreTooClose, isEnabledFlag } from "../helpers";
import useStore from "../store"
import OrderDetails from "./../components/OrderDetails.vue";
import BookingMoveModal from "./../components/BookingMoveModal.vue";
import BookingCancelModal from "./../components/BookingCancelModal.vue";
import api from "../api";
export default {
  components: {
    OrderDetails,
    BookingMoveModal,
    BookingCancelModal,
  },
  data() {
    return {
      actionSuccess: "",
      activeItem: null,
      activeItemInd: null,
      cancelError: "",
      cancelMode: "item",
      cancelSaving: false,
      moveError: "",
      moveSaving: false,
      showCancelModal: false,
      showMoveModal: false,
    };
  },
  computed: {
    ...mapState(useStore, ["options", "booking", "status", "quote", "attractions", "bookingNumber", "bookingToken"]),
    dateConfirmed() {
      return moment(this.booking.dateConfirmed).format("dddd, Do MMM YYYY - h:mma");
    },
    items() {
      return this.hasOrder
        ? this.booking.items.map((i) => {
            i.startDate = moment(i.startTime).format("dddd, Do MMM YYYY - h:mma");
            i.title =
                this.attractions.find((a) => a.rezdy_product_code === i.productCode)?.title ??
              i.productName;
            return i;
          })
        : [];
    },
    hasOrder() {
      return !!this.booking?.orderNumber;
    },
    isCancelledBooking() {
      return String(this.booking?.status ?? "").toUpperCase() === "CANCELLED";
    },
    pageTitle() {
      return this.isCancelledBooking
        ? "Your booking has been cancelled"
        : "Thank you for your booking";
    },
    pageIntro() {
      return this.isCancelledBooking
        ? "This booking is now cancelled."
        : "We can’t wait to see you!";
    },
    textContent() {
      if (this.isCancelledBooking) {

        if (this.booking?.retail_booking_status === "cancelled") {
          return `
            <p>Your cancellation has been processed for this booking.</p>
            <p>As this request has been processed outside of our <a href="/booking-policy/" class="underline" target="_blank">Terms and Conditions</a> refund period, we are unable to provide a refund on this occasion.
            If you have any questions or would like extra support, please contact our team on (08) 9757 7411 or send us a Whatsapp message on
            <a target="_blank" href="https://api.whatsapp.com/send?phone=+61412877120&text=I would like some help with the order ${this.booking.orderNumber}">+61 412 877 120</a></p>
            <p>We'd love to welcome you on a Capes Foundation experience in the future.</p>
          `;
        }

        return `
          <p>Your cancellation has been processed for this booking and you will receive a full refund.</p>
          <p>If you have any questions about refunds or would like extra support,
          please call us on <a href="tel:+61897577411">(08) 9757 7411</a> or send us a Whatsapp message on
          <a target="_blank" href="https://api.whatsapp.com/send?phone=+61412877120&text=I would like some help with the order ${this.booking.orderNumber}">+61 412 877 120</a>.</p>
          <p>We'd love to welcome you on a Capes Foundation experience in the future.</p>
        `;
      }

      return this.options.thankYouText;
    },
    bookingHasPastItems() {
      const items = this.booking?.items ?? [];

      return items.some((item) => {
        const startTime = item?.startTimeLocal ?? item?.startTime ?? null;

        return startTime ? moment(startTime).isBefore(moment()) : false;
      });
    },
  },
  methods: {
    attractionFromProductCode(productCode) {
      return this.attractions.find((attraction) => attraction.rezdy_retail_product_code === productCode) ?? null;
    },
    moveBookingsEnabled(item) {
      return isEnabledFlag(this.attractionFromProductCode(item?.productCode)?.enabled_move_bookings);
    },
    cancelBookingsEnabled(item) {
      return isEnabledFlag(this.attractionFromProductCode(item?.productCode)?.enabled_cancel_bookings);
    },
    moveBookingCutoffMinutes(item) {
      return Number(this.attractionFromProductCode(item?.productCode)?.move_booking_cutoff_minutes ?? 0);
    },
    itemIsWithinMoveCutoff(item) {
      const startTime = item?.startTimeLocal ?? item?.startTime ?? null;
      const cutoffMinutes = this.moveBookingCutoffMinutes(item);

      if (!startTime || cutoffMinutes <= 0) {
        return false;
      }

      return moment(startTime).diff(moment(), "minutes", true) <= cutoffMinutes;
    },
    movedBookingItems(nextTime, itemInd) {
      return (this.booking?.items ?? []).map((currentItem, index) => ({
        ...currentItem,
        time:
          index === Number(itemInd)
            ? nextTime
            : currentItem?.startTimeLocal ?? currentItem?.startTime ?? null,
      }));
    },
    quantitiesToParticipants(quantities = []) {
      return quantities.reduce((participants, quantity) => {
        participants[quantity.optionLabel] = Number(quantity.value ?? 0);
        return participants;
      }, {});
    },
    bookingItemToPayload(item, overrideTime = null, index = null, cancel = false) {
      const attraction = this.attractionFromProductCode(item.productCode);

      if (!attraction) {
        return null;
      }

      return {
        id: attraction.id,
        cancel,
        index,
        product_code: item.productCode,
        time: overrideTime ?? item.startTimeLocal ?? item.startTime,
        participants: this.quantitiesToParticipants(item.quantities),
        extras: Array.isArray(item.extras)
          ? item.extras.map((extra) => ({
              id: extra.id,
              name: extra.name,
              quantity: Number(extra.quantity ?? 1),
            }))
          : [],
      };
    },
    scrollToActiveModal() {
      this.$nextTick(() => {
        this.$refs.activeModal?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
    },
    scrollToThanksTop() {
      this.$nextTick(() => {
        this.$refs.thanksPage?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
    },
    openMoveModal(item, itemInd) {
      this.showCancelModal = false;
      this.activeItem = item;
      this.activeItemInd = itemInd;
      this.moveError = "";
      this.actionSuccess = "";
      this.showMoveModal = true;
      this.scrollToActiveModal();
    },
    closeModal(force = false) {
      if (this.moveSaving && !force) {
        return;
      }

      if (this.cancelSaving && !force) {
        return;
      }

      this.showCancelModal = false;
      this.activeItem = null;
      this.activeItemInd = null;
      this.cancelError = "";
      this.showMoveModal = false;
      this.moveError = "";
    },
    openCancelItemModal(item, itemInd) {
      this.showMoveModal = false;
      this.activeItem = item;
      this.activeItemInd = itemInd;
      this.cancelMode = "item";
      this.cancelError = "";
      this.actionSuccess = "";
      this.showCancelModal = true;
      this.scrollToActiveModal();
    },
    openCancelBookingModal() {
      this.showMoveModal = false;
      this.activeItem = null;
      this.activeItemInd = null;
      this.cancelMode = "booking";
      this.cancelError = "";
      this.actionSuccess = "";
      this.showCancelModal = true;
      this.scrollToActiveModal();
    },
    bookingNumberForUpdate() {
      return this.bookingNumber || this.booking.internal_id;
    },
    async updateBooking(payload) {
      const bookingNumber = this.bookingNumberForUpdate();

      if (!bookingNumber || !this.bookingToken) {
        throw new Error("We couldn’t verify your booking token. Please reload this page and try again.");
      }

      const response = await api.bookings.update(bookingNumber, this.bookingToken, payload);
      const store = useStore();
      const updatedBooking = response?.data?.booking ?? response?.data;

      if (updatedBooking?.items || updatedBooking?.orderNumber || updatedBooking?.internal_id) {
        store.setBooking(updatedBooking);
      } else {
        await store.loadBooking(bookingNumber, this.bookingToken);
      }

      return response;
    },
    async cancelBooking(payload) {
      const bookingNumber = this.bookingNumberForUpdate();

      if (!bookingNumber || !this.bookingToken) {
        throw new Error("We couldn’t verify your booking token. Please reload this page and try again.");
      }

      const response = await api.bookings.cancel(bookingNumber, this.bookingToken, payload);
      const store = useStore();
      const updatedBooking = response?.data?.booking ?? response?.data;

      if (updatedBooking?.items || updatedBooking?.orderNumber || updatedBooking?.internal_id) {
        store.setBooking(updatedBooking);
      } else {
        await store.loadBooking(bookingNumber, this.bookingToken);
      }

      return response;
    },
    buildCancellationPayload(mode, item = null, itemInd = null) {
      const bookingItems = this.booking?.items ?? [];
      const targetIndexes = bookingItems
        .map((currentItem, index) => ({ currentItem, index }))
        .filter(({ currentItem }) => {
          const startTime = currentItem?.startTimeLocal ?? currentItem?.startTime ?? null;
          return startTime ? moment(startTime).isAfter(moment()) : false;
        })
        .filter(({ index }) => mode === "booking" || index === itemInd)
        .map(({ index }) => index);

      return {
        action: "cancel",
        cancel_booking: mode === "booking",
        cancel_item_indexes: targetIndexes,
        token: this.bookingToken,
        items: bookingItems
          .map((currentItem, index) =>
            this.bookingItemToPayload(
              currentItem,
              null,
              index,
              targetIndexes.includes(index)
            )
          )
          .filter(Boolean),
      };
    },
    async recaptcha() {
      // Try twice - network glitches on mobile are often momentary
      for (let attempt = 1; attempt <= 2; attempt++) {
        try {
          await this.$recaptchaLoaded();
          return await this.$recaptcha("login");
        } catch (error) {
          console.warn(`reCAPTCHA attempt ${attempt}/2 failed:`, error);
          if (attempt < 2) {
            // Brief pause before retry
            await new Promise(resolve => setTimeout(resolve, 1000));
          } else {
            // Final attempt failed - continue without token
            // Server-side can handle missing reCAPTCHA
            return  "";
          }
        }
      }
    },
    async submitMove({ item, time, itemInd }) {
      this.moveError = "";
      this.actionSuccess = "";
      this.moveSaving = true;

      try {
        if (this.bookingHasPastItems) {
          throw new Error("This booking can’t be changed because one or more booking times are already in the past.");
        }

        if (!this.moveBookingsEnabled(item)) {
          throw new Error("This booking can’t be moved online.");
        }

        if (this.itemIsWithinMoveCutoff(item)) {
          throw new Error(`This booking can’t be moved within ${this.moveBookingCutoffMinutes(item)} minutes of the start time.`);
        }

        if (bookingItemsAreTooClose(this.movedBookingItems(time, itemInd))) {
          throw new Error("This time is too close to another attraction in your booking. Please allow more time for travel between activities.");
        }

        const payload = {
          "g-recaptcha-response" : await this.recaptcha(),
          token: this.bookingToken,
          items: (this.booking?.items ?? [])
            .map((currentItem, index) =>
              this.bookingItemToPayload(
                currentItem,
                itemInd === index ? time : null,
                index
              )
            )
            .filter(Boolean),
        };

        if (payload.items.length !== (this.booking?.items ?? []).length) {
          throw new Error("One of the booking items couldn’t be matched to an experience.");
        }

        await this.updateBooking(payload);

        this.moveSaving = false;
        this.actionSuccess = "Your booking time has been updated.";
        this.closeModal(true);
        this.scrollToThanksTop();

        if (window?.fathom) {
          window.fathom.trackEvent('booking-moved');
        }

      } catch (error) {
        this.moveError =
          error?.response?.data?.message ||
          error?.message ||
          "We couldn’t move this booking time right now. Please try again.";
      } finally {
        this.moveSaving = false;
      }
    },
    async submitCancel({ mode, item, itemInd }) {
      this.cancelError = "";
      this.actionSuccess = "";
      this.cancelSaving = true;

      try {
        if (mode === "booking" && (this.booking?.items ?? []).some((currentItem) => !this.cancelBookingsEnabled(currentItem))) {
          throw new Error("One or more booking items can’t be cancelled online.");
        }

        if (mode === "item" && !this.cancelBookingsEnabled(item)) {
          throw new Error("This booking item can’t be cancelled online.");
        }

        const payload = this.buildCancellationPayload(mode, item, itemInd);

        if (!payload.cancel_item_indexes.length) {
          throw new Error("There aren’t any upcoming booking items available to cancel.");
        }

        payload["g-recaptcha-response"] = await this.recaptcha();
        await this.cancelBooking(payload);

        this.actionSuccess =
          mode === "booking"
            ? "Your booking has been cancelled."
            : "Your booking item has been cancelled.";
        this.closeModal(true);
        this.scrollToThanksTop();

        if (window?.fathom) {
          window.fathom.trackEvent('booking-cancelled-' + (mode === "booking" ? "booking" : "item"));
        }

      } catch (error) {
        this.cancelError =
          error?.response?.data?.message ||
          error?.message ||
          "We couldn’t cancel this booking right now. Please try again.";
      } finally {
        this.cancelSaving = false;
      }
    },
  },
};
</script>
